import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { reverseGeocode } from '../lib/geo';

/** Used until we know where the visitor is. */
export const DEFAULT_LOCATION = {
  lat: 40.7127753,
  lng: -74.0059728,
  cc: 'us',
  name: 'New York, NY, USA',
  isDefault: true,
};

/**
 * The previous site asked for the visitor's location as soon as the page
 * loaded. We now only read it silently when permission was already granted,
 * and otherwise wait for a "Near me" / "Use my location" tap.
 * Set to true to restore the old prompt-on-load behaviour.
 */
const ASK_FOR_LOCATION_ON_LOAD = false;

const LocationContext = createContext(null);

export function LocationProvider({ children }) {
  // status: idle | locating | ready | denied | unavailable
  const [state, setState] = useState({ status: 'idle', location: null });

  const locate = useCallback(
    () =>
      new Promise((resolve) => {
        if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
          setState({ status: 'unavailable', location: null });
          resolve(null);
          return;
        }
        setState((current) => ({ ...current, status: 'locating' }));
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude: lat, longitude: lng } = position.coords;
            let place = { cc: '', name: '' };
            try {
              place = await reverseGeocode(lat, lng);
            } catch {
              // Keep coordinates even if Google can't name the place.
            }
            const location = { lat, lng, cc: place.cc || 'us', name: place.name || 'Near me' };
            setState({ status: 'ready', location });
            resolve(location);
          },
          (error) => {
            setState({ status: error.code === 1 ? 'denied' : 'unavailable', location: null });
            resolve(null);
          },
          { enableHighAccuracy: false, timeout: 12_000, maximumAge: 10 * 60 * 1000 }
        );
      }),
    []
  );

  useEffect(() => {
    let cancelled = false;
    if (ASK_FOR_LOCATION_ON_LOAD) {
      locate();
      return undefined;
    }
    navigator.permissions
      ?.query({ name: 'geolocation' })
      .then((permission) => {
        if (cancelled) return;
        if (permission.state === 'granted') locate();
        else if (permission.state === 'denied') setState({ status: 'denied', location: null });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [locate]);

  const value = useMemo(
    () => ({
      status: state.status,
      location: state.location,
      /** Visitor's location when known, else New York. */
      effective: state.location || DEFAULT_LOCATION,
      locate,
    }),
    [state, locate]
  );

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
}

export function useUserLocation() {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error('useUserLocation must be used inside <LocationProvider>');
  return ctx;
}
